import * as React from 'react';
import { getAdminApiBaseUrl } from '../utils/adminApiBaseUrl';
import { useState, useEffect, useRef } from 'react';
import * as echarts from 'echarts/core';
import { BarChart } from 'echarts/charts';
import {
    GridComponent,
    TooltipComponent,
    LegendComponent,
} from 'echarts/components';
import {
    LabelLayout,
    UniversalTransition,
} from 'echarts/features';
import { SVGRenderer } from 'echarts/renderers';
import { 
    Card,
    CardContent,
    Box,
    TextField,
    Grid,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Paper,
    Typography,
    CircularProgress
} from '@mui/material';
import DashboardPanelHeader from '../components/DashboardPanelHeader';
import { format, parseISO, subDays, startOfWeek, endOfWeek, startOfMonth, endOfMonth, startOfYear } from 'date-fns';
import useAuthorize from '../hooks/useAuthorize';
import { DASHBOARD_MODULE_ROLES } from '../roles/portalRoles';
import { AnalyticsFreshnessNote } from '../utils/AnalyticsFreshnessNote';
import ActivityTrendChart, { ActivityTrendPoint } from './ActivityTrendChart';
import axios from 'axios';

echarts.use([
    GridComponent,
    TooltipComponent,
    LegendComponent,
    BarChart,
    LabelLayout,
    UniversalTransition,
    SVGRenderer,
]);

interface ActivityStat {
    conversion_type: string;
    activity_count: number;
    first_activity: string;
    last_activity: string;
}

interface ActivitiesStatsResponse {
    data: ActivityStat[];
    total: number;
    limit: number;
    offset: number;
    data_through?: string;
    last_updated_at?: string | null;
}

interface ActivitiesTrendsResponse {
    series: Record<string, ActivityTrendPoint[]>;
    conversion_types?: string[];
    data_through?: string;
    last_updated_at?: string | null;
}

const FEATURED_ACTIVITY_CHARTS = [
    { key: 'signup', label: 'Signup', description: 'New customer accounts created', color: '#2e7d32' },
    { key: 'inquiry', label: 'Inquiry', description: 'Product inquiries placed on Credco', color: '#1565c0' },
    { key: 'wishlist_inquiry', label: 'Wishlist Inquiry', description: 'Inquiries started from a watchlist', color: '#6a1b9a' },
    { key: 'recycle_product', label: 'Recycle Listing', description: 'Recycle products listed by sellers', color: '#ef6c00' },
    { key: 'recycle_listing', label: 'Recycle Inquiries', description: 'View & Inquire clicks on reprocessed listings', color: '#ad1457' },
    { key: 'recycle_inquiry', label: 'Recycle Inquiry', description: 'Message, WhatsApp, or Call on a recycle listing', color: '#00838f' },
] as const;

const CONVERSION_TYPE_LABELS: Record<string, string> = {
    signup: 'Signup',
    inquiry: 'Inquiry',
    wishlist_inquiry: 'Wishlist Inquiry',
    recycle_listing: 'Recycle Inquiries',
    recycle_inquiry: 'Recycle Inquiry',
    recycle_product: 'Recycle Listing',
};

interface ActivityBarChartProps {
    data: ActivityStat[];
    formatLabel: (conversionType: string | null) => string;
}

const ActivityBarChart = ({ data, formatLabel }: ActivityBarChartProps) => {
    const chartRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        if (!chartRef.current) {
            return;
        }

        const chart = echarts.init(chartRef.current, undefined, { renderer: 'svg' });
        const sortedData = [...data].sort((a, b) => b.activity_count - a.activity_count);

        chart.setOption({
            tooltip: {
                trigger: 'axis',
                axisPointer: { type: 'shadow' },
                formatter: (params: any) => {
                    const item = Array.isArray(params) ? params[0] : params;
                    return `${item.name}<br/>Activities: ${Number(item.value || 0).toLocaleString()}`;
                },
            },
            grid: {
                left: 48,
                right: 24,
                top: 24,
                bottom: 72,
            },
            xAxis: {
                type: 'category',
                data: sortedData.map(item => formatLabel(item.conversion_type)),
                axisLabel: {
                    interval: 0,
                    rotate: sortedData.length > 4 ? 28 : 0,
                },
            },
            yAxis: {
                type: 'value',
                minInterval: 1,
            },
            series: [
                {
                    name: 'Activities',
                    type: 'bar',
                    data: sortedData.map(item => item.activity_count),
                    itemStyle: {
                        color: '#1976d2',
                        borderRadius: [4, 4, 0, 0],
                    },
                    label: {
                        show: true,
                        position: 'top',
                        formatter: ({ value }: { value: number }) => Number(value || 0).toLocaleString(),
                    },
                },
            ],
        });

        const handleResize = () => chart.resize();
        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
            chart.dispose();
        };
    }, [data, formatLabel]);

    return <Box ref={chartRef} sx={{ width: '100%', height: 320 }} />;
};

const DashboardActivities = () => {
    useAuthorize(DASHBOARD_MODULE_ROLES);
    
    // Default date range: first day of the current year through today.
    const getDefaultDateRange = () => {
        const now = new Date();
        const startDate = startOfYear(now);
        
        return {
            fromDate: format(startDate, 'yyyy-MM-dd'),
            toDate: format(now, 'yyyy-MM-dd')
        };
    };
    
    const defaultDates = getDefaultDateRange();
    
    const [period, setPeriod] = useState<'daily' | 'weekly' | 'monthly' | 'quarterly'>('monthly');
    const [fromDate, setFromDate] = useState<string>(defaultDates.fromDate);
    const [toDate, setToDate] = useState<string>(defaultDates.toDate);
    const [quickRange, setQuickRange] = useState<string>('');
    const [activitiesData, setActivitiesData] = useState<ActivityStat[]>([]);
    const [trendSeries, setTrendSeries] = useState<Record<string, ActivityTrendPoint[]>>({});
    const [loading, setLoading] = useState(true);
    const [trendsLoading, setTrendsLoading] = useState(true);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [dataThrough, setDataThrough] = useState<string | null>(null);
    const [lastUpdatedAt, setLastUpdatedAt] = useState<string | null>(null);

    const captureFreshness = (response: any) => {
        const nextDataThrough =
            response.data?.data_through ||
            response.headers?.['x-analytics-data-through'] ||
            null;
        const nextLastUpdated =
            response.data?.last_updated_at ||
            response.headers?.['x-analytics-last-updated'] ||
            null;
        if (nextDataThrough) {
            setDataThrough(nextDataThrough);
        }
        if (nextLastUpdated) {
            setLastUpdatedAt(nextLastUpdated);
        }
    };

    useEffect(() => {
        const fetchActivitiesData = async () => {
            setLoading(true);
            const apiUrl = `${getAdminApiBaseUrl()}/customers/activities-stats`;
            const params = {
                from_date: fromDate,
                to_date: toDate,
                limit: rowsPerPage,
                offset: page * rowsPerPage
            };
            
            try {
                const response = await axios.get<ActivitiesStatsResponse>(apiUrl, { params });
                
                if (response.data && response.data.data && Array.isArray(response.data.data)) {
                    setActivitiesData(response.data.data);
                    setTotal(response.data.total || 0);
                    captureFreshness(response);
                } else {
                    setActivitiesData([]);
                    setTotal(0);
                }
            } catch (error: any) {
                console.error('Error fetching activities stats:', error);
                setActivitiesData([]);
                setTotal(0);
            } finally {
                setLoading(false);
            }
        };

        fetchActivitiesData();
    }, [fromDate, toDate, page, rowsPerPage]);

    useEffect(() => {
        const fetchActivitiesTrends = async () => {
            setTrendsLoading(true);
            const apiUrl = `${getAdminApiBaseUrl()}/customers/activities-trends`;
            const params = {
                period,
                from_date: fromDate,
                to_date: toDate,
                conversion_types: FEATURED_ACTIVITY_CHARTS.map((item) => item.key).join(','),
            };

            try {
                const response = await axios.get<ActivitiesTrendsResponse>(apiUrl, { params });
                setTrendSeries(response.data?.series || {});
                captureFreshness(response);
            } catch (error: any) {
                console.error('Error fetching activities trends:', error);
                setTrendSeries({});
            } finally {
                setTrendsLoading(false);
            }
        };

        fetchActivitiesTrends();
    }, [period, fromDate, toDate]);

    const handlePeriodChange = (event: any) => {
        setPeriod(event.target.value);
    };

    const handleFromDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setFromDate(event.target.value);
        setQuickRange('');
        setPage(0);
    };

    const handleToDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setToDate(event.target.value);
        setQuickRange('');
        setPage(0);
    };

    const handleQuickRangeChange = (event: any) => {
        const range = event.target.value;
        setQuickRange(range);
        
        if (!range) {
            return;
        }

        const now = new Date();
        let from = now;
        let to = now;

        switch (range) {
            case 'today':
                from = to = now;
                break;
            case 'yesterday':
                from = to = subDays(now, 1);
                break;
            case 'last7':
                from = subDays(now, 6);
                to = now;
                break;
            case 'thisWeek':
                from = startOfWeek(now, { weekStartsOn: 1 });
                to = endOfWeek(now, { weekStartsOn: 1 });
                break;
            case 'thisMonth':
                from = startOfMonth(now);
                to = endOfMonth(now);
                break;
            default:
                break;
        }

        setFromDate(format(from, 'yyyy-MM-dd'));
        setToDate(format(to, 'yyyy-MM-dd'));
        setPage(0);
    };

    const handleChangePage = (_event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const formatDate = (dateString: string) => {
        try {
            const date = parseISO(dateString);
            return format(date, 'MMM dd, yyyy HH:mm');
        } catch (e) {
            return dateString;
        }
    };

    const formatConversionType = (conversionType: string | null) => {
        if (!conversionType) return '(empty)';
        if (CONVERSION_TYPE_LABELS[conversionType]) {
            return CONVERSION_TYPE_LABELS[conversionType];
        }
        return conversionType
            .split('_')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join(' ');
    };

    const getTrendGrowth = (points: ActivityTrendPoint[] = []) => {
        if (points.length < 2) {
            return null;
        }
        const previous = Number(points[points.length - 2]?.count) || 0;
        const current = Number(points[points.length - 1]?.count) || 0;
        if (previous === 0) {
            return current > 0 ? 100 : 0;
        }
        return ((current - previous) / previous) * 100;
    };

    const exportDateParams = { from_date: fromDate, to_date: toDate, period };

    return (
        <Box sx={{ padding: 2 }}>
            <Card>
                <DashboardPanelHeader
                    title="Activities Statistics"
                    subheader="Conversion totals plus period trends for growth comparison"
                    exportFilename="activities-detail"
                    exportPanel="activities_detail"
                    exportParams={exportDateParams}
                />
                <CardContent>
                    <Grid container spacing={2} sx={{ marginBottom: 3 }}>
                        <Grid item xs={12} sm={3}>
                            <TextField
                                label="From Date"
                                type="date"
                                value={fromDate}
                                onChange={handleFromDateChange}
                                InputLabelProps={{
                                    shrink: true,
                                }}
                                fullWidth
                                size="small"
                            />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                            <TextField
                                label="To Date"
                                type="date"
                                value={toDate}
                                onChange={handleToDateChange}
                                InputLabelProps={{
                                    shrink: true,
                                }}
                                fullWidth
                                size="small"
                            />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                            <FormControl variant="outlined" fullWidth size="small">
                                <InputLabel>Period</InputLabel>
                                <Select
                                    value={period}
                                    onChange={handlePeriodChange}
                                    label="Period"
                                >
                                    <MenuItem value="daily">Daily</MenuItem>
                                    <MenuItem value="weekly">Weekly</MenuItem>
                                    <MenuItem value="monthly">Monthly</MenuItem>
                                    <MenuItem value="quarterly">Quarterly</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={3}>
                            <FormControl variant="outlined" fullWidth size="small">
                                <InputLabel>Quick Range</InputLabel>
                                <Select value={quickRange} onChange={handleQuickRangeChange} label="Quick Range">
                                    <MenuItem value="">Custom</MenuItem>
                                    <MenuItem value="today">Today</MenuItem>
                                    <MenuItem value="yesterday">Yesterday</MenuItem>
                                    <MenuItem value="last7">Last 7 Days</MenuItem>
                                    <MenuItem value="thisWeek">This Week</MenuItem>
                                    <MenuItem value="thisMonth">This Month</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>

                    {dataThrough && (
                        <AnalyticsFreshnessNote dataThrough={dataThrough} lastUpdatedAt={lastUpdatedAt} />
                    )}

                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', padding: 4 }}>
                            <CircularProgress />
                        </Box>
                    ) : activitiesData.length === 0 ? (
                        <Box sx={{ padding: 4, textAlign: 'center' }}>
                            <Typography variant="body1" color="text.secondary">
                                No activities data available for the selected date range
                            </Typography>
                        </Box>
                    ) : (
                        <>
                            <Paper variant="outlined" sx={{ marginBottom: 3, padding: 2 }}>
                                <DashboardPanelHeader
                                    title="Activity Counts"
                                    subheader="Shows how many times each activity happened in the selected date range."
                                    exportFilename="activity-counts-detail"
                                    exportPanel="activities_detail"
                                    exportParams={exportDateParams}
                                />
                                <ActivityBarChart data={activitiesData} formatLabel={formatConversionType} />
                            </Paper>
                            <TableContainer component={Paper}>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell><strong>Rank</strong></TableCell>
                                            <TableCell><strong>Conversion Type</strong></TableCell>
                                            <TableCell align="right"><strong>Activity Count</strong></TableCell>
                                            <TableCell><strong>First Activity</strong></TableCell>
                                            <TableCell><strong>Last Activity</strong></TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {activitiesData.map((row, index) => (
                                            <TableRow key={row.conversion_type || `empty-${index}`} hover>
                                                <TableCell>
                                                    {page * rowsPerPage + index + 1}
                                                </TableCell>
                                                <TableCell>
                                                    <Typography 
                                                        variant="body2" 
                                                        sx={{ 
                                                            fontWeight: 'medium'
                                                        }}
                                                    >
                                                        {formatConversionType(row.conversion_type)}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell align="right">
                                                    <Typography variant="body2" fontWeight="bold">
                                                        {row.activity_count.toLocaleString()}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2" color="text.secondary">
                                                        {formatDate(row.first_activity)}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2" color="text.secondary">
                                                        {formatDate(row.last_activity)}
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            <TablePagination
                                component="div"
                                count={total}
                                page={page}
                                onPageChange={handleChangePage}
                                rowsPerPage={rowsPerPage}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                                rowsPerPageOptions={[10, 25, 50, 100]}
                            />
                        </>
                    )}

                    <Box sx={{ marginTop: 3 }}>
                        <DashboardPanelHeader
                            title="Activity Growth Trends"
                            subheader={`Individual ${period} series for signup, inquiry, wishlist inquiry, recycle listing, recycle inquiries, and recycle inquiry. Use Period + date filters to compare growth across buckets.`}
                            exportFilename="activity-growth-trends-detail"
                            exportPanel="activities_detail"
                            exportParams={exportDateParams}
                        />

                        {trendsLoading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', padding: 4 }}>
                                <CircularProgress />
                            </Box>
                        ) : (
                            <Grid container spacing={2}>
                                {FEATURED_ACTIVITY_CHARTS.map((chart) => {
                                    const points = trendSeries[chart.key] || [];
                                    const growth = getTrendGrowth(points);
                                    const latest = points.length ? Number(points[points.length - 1].count) || 0 : 0;
                                    return (
                                        <Grid item xs={12} md={6} key={chart.key}>
                                            <Paper variant="outlined" sx={{ padding: 2, height: '100%' }}>
                                                <DashboardPanelHeader
                                                    title={chart.label}
                                                    subheader={`${chart.description}. Latest: ${latest.toLocaleString()}${growth === null ? '' : ` · ${growth >= 0 ? '+' : ''}${growth.toFixed(1)}%`}`}
                                                    exportFilename={`activity-trend-${chart.key}`}
                                                    exportPanel="activities_detail"
                                                    exportParams={{ ...exportDateParams, conversion_type: chart.key }}
                                                />
                                                {points.length === 0 ? (
                                                    <Box sx={{ height: 260, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                        <Typography variant="body2" color="text.secondary">
                                                            No {chart.label.toLowerCase()} data in this range
                                                        </Typography>
                                                    </Box>
                                                ) : (
                                                    <ActivityTrendChart
                                                        data={points}
                                                        period={period}
                                                        label={chart.label}
                                                        color={chart.color}
                                                    />
                                                )}
                                            </Paper>
                                        </Grid>
                                    );
                                })}
                            </Grid>
                        )}
                    </Box>
                </CardContent>
            </Card>
        </Box>
    );
};

export default DashboardActivities;
